import { NOOP } from '@lemonade-hq/ts-helpers';
import type { DialogProps as RadixDialogProps } from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import type { PropsWithChildren, ReactElement } from 'react';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Dialog } from '../components/Dialog/Dialog';
import { Drawer } from '../components/Drawer/Drawer';
import { Flex } from '../components/Flex/Flex';
import type { IconName } from '../components/Icon/Icon';
import { IconButton } from '../components/IconButton/IconButton';
import { useViewportSize } from '../hooks/useViewportSize';
import * as styles from './ModalProvider.css';

// from @radix-ui/react-dialog
type PointerDownOutsideEvent = CustomEvent<{
  readonly originalEvent: PointerEvent;
}>;

type FocusOutsideEvent = CustomEvent<{
  readonly originalEvent: FocusEvent;
}>;

type DialogEvent = FocusOutsideEvent | KeyboardEvent | PointerDownOutsideEvent;

interface DialogProps extends RadixDialogProps {
  readonly size?: 'large' | 'small';
  readonly dynamicHeight?: boolean;
}

export interface TopAction {
  readonly icon: IconName;
  readonly onClick: () => void;
}
export interface ModalProps<TModalProps = Record<string, unknown>> {
  readonly title?: string;
  readonly preventBackdropClose?: boolean;
  readonly fullscreen?: boolean;
  readonly dismissible?: boolean;
  readonly disclaimer?: string;
  readonly component: React.FC<TModalProps>;
  readonly props: TModalProps;
  readonly topAction?: TopAction;
  /**
   * Controls close button visibility on mobile. By default, close button is hidden on mobile except in fullscreen mode.
   * Set to `true` only for internal edge cases where explicit user dismissal is required (e.g., survey dialogs).
   * @default false
   */
  readonly showCloseButtonForMobile?: boolean;
  readonly enableScroll?: boolean;
}

interface ModalContextValue {
  readonly isOpen: boolean;
  readonly open: <TModalProps = Record<string, unknown>>(props: DialogProps & ModalProps<TModalProps>) => void;
  readonly close: () => void;
}

const ModalContext = createContext<ModalContextValue>({
  isOpen: false,
  open: NOOP,
  close: NOOP,
});

const IsomorphicModal: React.FC<DialogProps & Omit<ModalProps, 'component' | 'props'>> = ({
  children,
  open,
  onOpenChange,
  fullscreen,
  dismissible = true,
  size,
  dynamicHeight,
  topAction,
  showCloseButtonForMobile = false,
  ...modalOptions
}) => {
  const { isMobileViewport } = useViewportSize();

  const isDismissible = fullscreen ? false : dismissible;
  const shouldShowCloseButton = (showCloseButtonForMobile || fullscreen) && dismissible;

  const handleDrawerOpenChange = useCallback(
    (value: boolean) => {
      if (!value && !dismissible) return;

      onOpenChange?.(value);
    },
    [dismissible, onOpenChange],
  );

  const preventDialogClose = useCallback(
    (e: DialogEvent) => {
      if (!dismissible) e.preventDefault();
    },
    [dismissible],
  );

  /* istanbul ignore next -- @preserve - we don't render content in tests  */
  const topActionComponent =
    topAction != null ? (
      <IconButton
        background="transparent"
        className={styles.iconButton()}
        icon={topAction.icon}
        onClick={topAction.onClick}
        size="lg"
      />
    ) : null;

  if (isMobileViewport) {
    return (
      <Drawer.Root onOpenChange={handleDrawerOpenChange} open={open} {...modalOptions} dismissible={isDismissible}>
        <Drawer.Content
          className={styles.drawerContent}
          description={modalOptions.title ?? 'Modal'}
          fullscreen={fullscreen}
          title={modalOptions.title ?? 'Modal'}
        >
          <Flex alignItems="center" width="100%">
            {topActionComponent}

            {shouldShowCloseButton && (
              <IconButton
                background="transparent"
                className={styles.iconButton({ rightAligned: true })}
                icon="close"
                onClick={() => onOpenChange?.(false)}
                size="lg"
              />
            )}
          </Flex>

          {React.Children.map(children as ReactElement, (child: ReactElement) =>
            React.cloneElement(child, { fullscreen }),
          )}
        </Drawer.Content>
      </Drawer.Root>
    );
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open} {...modalOptions}>
      <Dialog.Overlay />
      <Dialog.Content
        dynamicHeight={dynamicHeight}
        onEscapeKeyDown={preventDialogClose}
        onInteractOutside={preventDialogClose}
        size={size}
      >
        {dismissible && <Dialog.Controls>{topActionComponent}</Dialog.Controls>}

        <VisuallyHidden.Root>
          <Dialog.Title>{modalOptions.title ?? 'Modal'}</Dialog.Title>
          <Dialog.Description>{modalOptions.title ?? 'Modal'}</Dialog.Description>
        </VisuallyHidden.Root>

        {children}
      </Dialog.Content>
    </Dialog.Root>
  );
};

export const ModalProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [{ component: Component, props, onOpenChange, ...dialogOptions }, setModal] = useState<
    DialogProps & ModalProps
  >({
    component: /* istanbul ignore next -- not covered even if accessed for some reason */ () => null,
    props: { onClose: NOOP },
  });

  const onChange = useCallback(
    (newState: boolean) => {
      onOpenChange?.(newState);
      setIsOpen(newState);
    },
    [onOpenChange],
  );

  const close = useCallback(() => {
    onChange(false);
  }, [onChange]);

  const open = useCallback(
    <TModalProps extends Record<string, unknown> = Record<string, unknown>>(
      options: DialogProps & ModalProps<TModalProps>,
    ) => {
      setModal(options as unknown as DialogProps & ModalProps);
      onChange(true);
    },
    [onChange],
  );

  const contextValue = useMemo(() => ({ isOpen, open, close }), [isOpen, open, close]);

  return (
    <ModalContext.Provider value={contextValue as ModalContextValue}>
      {children}

      <IsomorphicModal {...dialogOptions} onOpenChange={onChange} open={isOpen}>
        <Component {...props} />
      </IsomorphicModal>
    </ModalContext.Provider>
  );
};

export function useModal(): ModalContextValue {
  return useContext(ModalContext);
}
